import classNames from "classnames";
import { concat, uniqBy } from "lodash";
import { SkillsList } from "PFApp/components/skills_list";
import { Divider } from "PFComponents/divider";
import { Typography } from "PFComponents/typography";
import useIsFeatureEnabled from "PFCore/helpers/use_is_feature_enabled";
import { useSkillsFrameworkMatchingProfileSkills } from "PFCore/hooks/queries/skills_frameworks/use_skills_framework_matching_profile_skills";
import { useSkillsFrameworksOptions } from "PFCore/hooks/queries/skills_frameworks/use_skills_frameworks_options";
import { SkillsFramework } from "PFTypes";
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";

import { InfoSection } from "../../components/info_section";
import { useMatchProfileInfo } from "../../hooks/use_match_profile_info";
import { useMatchCardContext } from "../../match_card_context";
import { MatchActivitySkills } from "../match_activity_skills";
import css from "./match_card_body.module.scss";

export const MatchCardBody = (): JSX.Element | null => {
  const {
    isOpen,
    isCompressed,
    custom_values: customValues,
    id: profileId,
    role,
    animate
  } = useMatchCardContext();
  const { sideInfo } = useMatchProfileInfo();
  const { t } = useTranslation("activities", { keyPrefix: "show.matches.card" });

  const relatedAndIndirectAttributes = uniqBy(
    concat(customValues?.related || [], customValues?.indirect || []),
    "id"
  );
  const isFeatureEnabled = useIsFeatureEnabled();
  const isSkillsFrameworkFeatureEnabled = isFeatureEnabled("skills_framework");

  const { data: skillsInFramework } = useSkillsFrameworkMatchingProfileSkills(
    { profileId, skillsFrameworkId: role.skills_framework_id! },
    { enabled: isSkillsFrameworkFeatureEnabled && !!role.skills_framework_id }
  );
  const { data: skillsFrameworkOptions } = useSkillsFrameworksOptions(
    { ids: [role.skills_framework_id!] },
    {
      enabled: isSkillsFrameworkFeatureEnabled && !!role.skills_framework_id
    }
  );

  const skillFrameworkName = (skillsFrameworkOptions?.entries as SkillsFramework[])?.find(
    ({ id }) => id === role.skills_framework_id
  )?.name;

  const isSkillsFrameworkSectionVisible =
    isSkillsFrameworkFeatureEnabled && !!skillsInFramework?.length && !!skillFrameworkName;

  useEffect(() => {
    // to fix the card height when skillsInFramework are queried
    animate();
  }, [isSkillsFrameworkSectionVisible, animate]);

  return (
    <div className={classNames(css.cardBodyContainer, { [css.closed]: !isOpen })}>
      <Divider color="paletteNeutral0" />
      <div className={css.cardBody}>
        <div className={css.sideArea}>
          {sideInfo.map(({ customType, value }) => (
            <InfoSection title={customType.display_as || customType.name} titleGray key={customType.id}>
              <Typography variant="labelBold">{value}</Typography>
            </InfoSection>
          ))}
        </div>
        <div className={css.mainArea}>
          {isCompressed && <MatchActivitySkills />}
          {!!relatedAndIndirectAttributes.length && (
            <InfoSection title={t("relatedAndIndirectAttributes")}>
              <SkillsList
                availableSkills={relatedAndIndirectAttributes}
                profileId={profileId}
                onMoreLessButtonClick={animate}
              />
            </InfoSection>
          )}
          {isSkillsFrameworkSectionVisible && (
            <InfoSection title={t("skillsInFramework", { name: skillFrameworkName })}>
              <SkillsList
                availableSkills={skillsInFramework}
                profileId={profileId}
                onMoreLessButtonClick={animate}
              />
            </InfoSection>
          )}
        </div>
      </div>
    </div>
  );
};
